import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Home, KeyRound, Link2, MapPin, Search, ShieldAlert, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { api, errMsg } from "../../lib/api";
import { pokeLiveCollections } from "../../lib/liveCollections";
import { useAuth } from "../../context/AuthContext";
import { useRoleModules } from "../../lib/roleModules";

/**
 * Phase 205 — Link Your Properties.
 * Onboarding variant (`?onboarding=1`, right after registration): step header + "Skip for now".
 * Later variant (from the dashboard / My Properties): same flow without the onboarding chrome.
 * Flow: enter PIN → Verify → confirm the address / zone / ward / type → Link → link another.
 * Conflict (already linked to a different mobile): the exact message is shown and linking is blocked.
 */
export function LinkPropertyPanel({ code, onLinked, testId = "link-property", autoFocus = true }) {
  const [pin, setPin] = useState("");
  const [checking, setChecking] = useState(false);
  const [linking, setLinking] = useState(false);
  const [result, setResult] = useState(null);     // verify-pin response
  const [error, setError] = useState(null);       // {title, message, conflict}
  const inputRef = useRef(null);
  const access = useRoleModules(code);
  const canLink = access.canWrite("properties", "my_properties");

  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);

  const reset = () => { setPin(""); setResult(null); setError(null); inputRef.current?.focus(); };

  const verify = async (e) => {
    e?.preventDefault();
    const v = pin.trim();
    if (v.length < 4) return toast.error("Enter the PIN printed on your demand notice or receipt");
    setChecking(true); setResult(null); setError(null);
    try {
      const r = await api.get(`/t/${code}/citizen/verify-pin`, { params: { pin: v } });
      if (!r.data.can_link) {
        setError({ title: "This property cannot be linked", message: r.data.message, conflict: r.data.status === "conflict", pin: r.data.pin });
      } else {
        setResult(r.data);
      }
    } catch (err) {
      const status = err?.response?.status;
      setError({ title: status === 404 ? "PIN not found" : "Could not verify this PIN", message: errMsg(err), conflict: false, pin: v });
    } finally { setChecking(false); }
  };

  const link = async () => {
    if (!result?.property) return;
    setLinking(true);
    try {
      const r = await api.post(`/t/${code}/citizen/link-property`, { pin: result.property.pin });
      pokeLiveCollections();
      toast.success(r.data.already ? `${r.data.pin} is already linked to your account` : `Property ${r.data.pin} linked to your account`);
      onLinked?.({ ...result.property, already: r.data.already });
      reset();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) setError({ title: "This property cannot be linked", message: errMsg(err), conflict: true, pin: result.property.pin });
      else toast.error(errMsg(err));
      setResult(null);
    } finally { setLinking(false); }
  };

  const p = result?.property;
  return (
    <div className="space-y-4" data-testid={`${testId}-panel`}>
      <form onSubmit={verify} className="space-y-2">
        <Label htmlFor={`${testId}-pin`}>Property Identification Number (PIN)</Label>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <KeyRound className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input id={`${testId}-pin`} ref={inputRef} value={pin} onChange={(e) => { setPin(e.target.value.toUpperCase()); if (error) setError(null); }}
              placeholder="e.g. BMC100001" className="pl-9 font-mono uppercase placeholder:normal-case h-11" autoComplete="off" spellCheck={false}
              data-testid={`${testId}-pin-input`} />
          </div>
          <Button type="submit" className="h-11" disabled={checking || pin.trim().length < 4} data-testid={`${testId}-verify-button`}>
            <Search className="h-4 w-4 mr-2" aria-hidden="true" /> {checking ? "Verifying…" : "Verify PIN"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">The PIN is printed on your property tax demand notice and on every receipt.</p>
      </form>

      {error && (
        <Alert variant="destructive" data-testid={`${testId}-error`} role="alert">
          <ShieldAlert className="h-4 w-4" aria-hidden="true" />
          <AlertTitle>{error.title}{error.pin ? <span className="font-mono font-normal"> · {error.pin}</span> : null}</AlertTitle>
          <AlertDescription data-testid={`${testId}-error-message`}>{error.message}</AlertDescription>
        </Alert>
      )}

      {p && (
        <Card className="border-primary/30 bg-primary/5 shadow-none" data-testid={`${testId}-confirm-card`}>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Is this your property?</p>
                <p className="font-mono font-semibold text-lg" data-testid={`${testId}-confirm-pin`}>{p.pin}</p>
                {p.owner_name && <p className="text-sm font-medium" data-testid={`${testId}-confirm-owner`}>{p.owner_name}</p>}
                <p className="text-sm text-muted-foreground flex items-start gap-1 mt-0.5" data-testid={`${testId}-confirm-address`}>
                  <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" aria-hidden="true" /> {p.address || "Address not recorded"}
                </p>
              </div>
              {result.status === "already_yours" && <Badge variant="outline" data-testid={`${testId}-already-badge`}>Already linked</Badge>}
            </div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded-md bg-background border px-2 py-1" data-testid={`${testId}-confirm-zone`}><span className="text-muted-foreground">Zone</span> <span className="font-medium">{p.zone_label || "—"}</span></span>
              <span className="rounded-md bg-background border px-2 py-1" data-testid={`${testId}-confirm-ward`}><span className="text-muted-foreground">Ward</span> <span className="font-medium">{p.ward_label || "—"}</span></span>
              {p.mohalla_label && <span className="rounded-md bg-background border px-2 py-1"><span className="text-muted-foreground">Mohalla</span> <span className="font-medium">{p.mohalla_label}</span></span>}
              {(p.property_type_label || p.usage_type_label) && <span className="rounded-md bg-background border px-2 py-1" data-testid={`${testId}-confirm-type`}><span className="text-muted-foreground">Type</span> <span className="font-medium">{p.property_type_label || p.usage_type_label}</span></span>}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={link} disabled={linking || !canLink} data-testid={`${testId}-confirm-button`}>
                <Link2 className="h-4 w-4 mr-2" aria-hidden="true" /> {linking ? "Linking…" : result.status === "already_yours" ? "Keep it linked" : "Yes, link this property"}
              </Button>
              <Button variant="ghost" onClick={reset} disabled={linking} data-testid={`${testId}-not-mine-button`}>Not mine — try another PIN</Button>
              {!canLink && <p className="text-xs text-muted-foreground" data-testid={`${testId}-no-permission`}>Linking is disabled for citizen accounts by your municipality.</p>}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function LinkProperties() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const onboarding = params.get("onboarding") === "1";
  const navigate = useNavigate();
  const { auth } = useAuth();
  const [linked, setLinked] = useState([]);      // linked in this session
  const [existing, setExisting] = useState(null);

  useEffect(() => {
    api.get(`/t/${code}/citizen/my-properties`).then((r) => setExisting(r.data)).catch(() => setExisting([]));
  }, [code, linked.length]);

  const onLinked = (p) => setLinked((xs) => (xs.some((x) => x.pin === p.pin) ? xs : [...xs, p]));
  const goHome = () => navigate(`/${code}/portal`);
  const firstName = auth?.user?.name?.split(" ")[0];

  return (
    <div className={`space-y-6 ${onboarding ? "max-w-2xl mx-auto" : ""}`} data-testid="link-properties-page">
      {onboarding ? (
        <div className="text-center space-y-2 pt-2" data-testid="link-properties-onboarding-header">
          <div className="inline-flex items-center gap-2 text-xs font-medium text-primary bg-primary/10 rounded-full px-3 py-1">
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Step 2 of 2 · Account created
          </div>
          <h1 className="text-2xl font-semibold font-heading">Welcome{firstName ? `, ${firstName}` : ""} — link your properties</h1>
          <p className="text-sm text-muted-foreground">Enter the PIN of each property you own to see its dues, receipts and your collector's contact in one place. You can link as many as you like now, or add more later from your dashboard.</p>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold font-heading">Link a property</h1>
            <p className="text-muted-foreground text-sm">Verify the PIN, confirm the address and link it to your account.</p>
          </div>
          <Button variant="outline" asChild><Link to={`/${code}/portal`} data-testid="link-properties-back-home"><Home className="h-4 w-4 mr-2" aria-hidden="true" /> Dashboard</Link></Button>
        </div>
      )}

      <Card className="shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-heading flex items-center gap-2"><Link2 className="h-4 w-4" aria-hidden="true" /> {linked.length ? "Link another property" : "Find your property"}</CardTitle>
          <CardDescription>One PIN at a time — we show the address and zone so you can confirm it is yours before linking.</CardDescription>
        </CardHeader>
        <CardContent>
          <LinkPropertyPanel code={code} onLinked={onLinked} testId="link-property" />
        </CardContent>
      </Card>

      {linked.length > 0 && (
        <Card className="shadow-sm border-[hsl(var(--success))]/30" data-testid="link-properties-session-list">
          <CardContent className="p-4 space-y-2">
            <p className="text-sm font-medium flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--success))]" aria-hidden="true" /> Linked in this session ({linked.length})</p>
            {linked.map((p) => (
              <div key={p.pin} className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2" data-testid={`link-properties-linked-${p.pin}`}>
                <div className="min-w-0">
                  <p className="font-mono text-sm font-medium">{p.pin}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.address || "—"} · {p.zone_label || "—"}{p.ward_label ? ` · ${p.ward_label}` : ""}</p>
                </div>
                <Badge variant="outline" className="shrink-0">{p.already ? "Already linked" : "Linked"}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-muted-foreground" data-testid="link-properties-total">
          {existing === null ? "" : existing.length === 0 ? "No properties linked to your account yet." : `${existing.length} propert${existing.length === 1 ? "y" : "ies"} linked to your account.`}
        </p>
        <div className="flex items-center gap-2">
          {onboarding && linked.length === 0 && (
            <Button variant="ghost" onClick={goHome} data-testid="link-properties-skip-button">Skip for now</Button>
          )}
          <Button onClick={goHome} variant={linked.length ? "default" : "outline"} data-testid="link-properties-done-button">
            Go to my dashboard <ArrowRight className="h-4 w-4 ml-2" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </div>
  );
}
